/**
 * Camada de capability do app desktop (Electron).
 *
 * REGRA: leia SEMPRE em use time (render/callback), nunca no escopo do módulo —
 * o preload injeta `window.__ODIN_DESKTOP__` antes do bundle, mas tratar isso
 * como constante de módulo é a mesma armadilha das feature flags.
 *
 * No web, tudo aqui é no-op/false. Nenhum caminho do app deve QUEBRAR sem a
 * ponte: o desktop só adiciona superfície, nunca remove capacidade.
 */

/** A ponte do preload, ou null no browser. */
export const getDesktopBridge = () =>
  typeof window !== "undefined" ? window.__ODIN_DESKTOP__ || null : null;

/** true só quando rodando dentro do shell Electron. */
export const isDesktopApp = () => !!getDesktopBridge();

/**
 * Plataforma corrente, granularidade grossa: "desktop" (shell Electron) ou "web".
 * Base do targeting por plataforma das feature flags (`isFeatureEnabled` em
 * settings.jsx). Estenda a união (ex.: "mobile", "mac", "win") só quando uma
 * feature concreta precisar — hoje só distinguimos web vs desktop. Leia SEMPRE
 * em use time (render/callback), nunca no escopo do módulo.
 * @returns {"web"|"desktop"}
 */
export const getPlatform = () => (isDesktopApp() ? "desktop" : "web");

/**
 * true quando o SO é Windows. Usa `userAgentData.platform` (Chromium moderno) e
 * cai para o sniff de UA (mesmo critério de `classifyDevice` em clientSpecs.js).
 * Leia SEMPRE em use time.
 * @returns {boolean}
 */
export const isWindows = () => {
  if (typeof navigator === "undefined") return false;
  const uad = navigator.userAgentData;
  if (uad && typeof uad.platform === "string")
    return uad.platform === "Windows";
  return /Windows/i.test(navigator.userAgent || "");
};

/**
 * Gate do acesso aos "gráficos antigos": SÓ no navegador (web) rodando em
 * Windows. Exclui o app nativo (desktop/Electron) em qualquer SO, e exclui
 * mac / mobile / tablet (nenhum é Windows). Regra de produto — leia em use time.
 * @returns {boolean}
 */
export const isWebOnWindows = () => getPlatform() === "web" && isWindows();

/**
 * Publica o estado do menu nativo (no-op no web).
 * @param {{items: Array<{id: string|number, label: string, dirty: boolean, active: boolean}>}} state
 */
export const publishFavoritesMenu = (state) => {
  getDesktopBridge()?.favorites?.publish?.(state);
};

/**
 * Assina os comandos vindos do menu nativo. Devolve a função de cleanup
 * (no web devolve um no-op, então o useEffect não precisa de guarda).
 * @param {(command: {type: string, id?: string|number}) => void} handler
 * @returns {() => void}
 */
export const onFavoritesCommand = (handler) => {
  const off = getDesktopBridge()?.favorites?.onCommand?.(handler);
  return typeof off === "function" ? off : () => {};
};

/**
 * Menu Nitro → Sair. Devolve cleanup (no-op no web).
 * @param {() => void} handler
 * @returns {() => void}
 */
export const onLogout = (handler) => {
  const off = getDesktopBridge()?.onLogout?.(handler);
  return typeof off === "function" ? off : () => {};
};

/**
 * Cofre seguro do SO (keychain via safeStorage) para "lembrar senha" no desktop.
 * No web, todas as operações são no-op/null — o caller cai no fallback (guardar
 * só broker+usuário em localStorage, nunca a senha).
 */
export const secretStore = {
  isAvailable: () => !!getDesktopBridge()?.secret,
  set: (key, value) =>
    getDesktopBridge()?.secret?.set?.(key, value) ?? Promise.resolve(false),
  get: (key) => getDesktopBridge()?.secret?.get?.(key) ?? Promise.resolve(null),
  remove: (key) =>
    getDesktopBridge()?.secret?.remove?.(key) ?? Promise.resolve(false),
};
